/**
 * Narrator abstraction.
 *
 * The contract is playback-level, not buffer-level: Web Speech queues to the
 * system output and can never hand back samples, so a `synthesize(): AudioBuffer`
 * contract would be unimplementable by half the implementations.
 *
 * The implementations differ in capabilities, which is why this is an interface
 * rather than test scaffolding:
 *
 *   cached/broadcast  graphRoutable, volumeControl, seekable, duckable
 *   Web Speech        none of the above
 */

export interface NarratorCapabilities {
  /** Can the output enter the Web Audio graph? */
  graphRoutable: boolean;
  volumeControl: boolean;
  /** Can playback start at a sample offset (mid-slot join)? */
  seekable: boolean;
  duckable: boolean;
}

export interface PrepareRequest {
  text: string;
  slotIndex: number;
  entryId: string;
  /** Synthesis units from normalization; each already <= the chunk cap. */
  chunks?: string[];
}

export interface PreparedNarration {
  /** Carried through so the player can verify freshness before playing. */
  slotIndex: number;
  entryId: string;
  text: string;
  /** Present only for graph-routable narrators. */
  buffer?: AudioBuffer;
  /** Seconds of audio, 0 when the narrator cannot report it. */
  durationSec: number;
}

export interface PlayOptions {
  /** Seconds into the narration at which to begin. Freshly computed by the player. */
  atOffsetSec: number;
  /** Where in narration time this playback should be at `contextTime`. */
  sync?: { contextTime: number; narrationTimeSec: number };
  onEnd: () => void;
}

export interface PlaybackHandle {
  stop(): void;
  readonly ended: boolean;
  readonly startedAtSec: number;
  readonly offsetSec: number;
}

export interface Narrator {
  readonly id: "buffer" | "webspeech" | "cached";
  readonly capabilities: NarratorCapabilities;
  init(): Promise<void>;
  prepare(request: PrepareRequest, signal: AbortSignal): Promise<PreparedNarration>;
  play(prepared: PreparedNarration, options: PlayOptions): PlaybackHandle;
  dispose(): void;
}

export type NegotiationResult =
  | { kind: "buffer" | "system"; narrator: Narrator }
  | { kind: "none"; reason: "no-local-voice" | "unsupported" };

export interface SpeechVoiceLike {
  name: string;
  lang: string;
  /** True for voices installed on the device. */
  localService: boolean;
  default?: boolean;
}

/**
 * A remote English voice is rejected on purpose. Claiming offline capability
 * while depending on a network voice would be a lie, and the whole point of the
 * fallback is a station that still speaks without a connection.
 */
export function isLocalEnglishVoice(voice: SpeechVoiceLike): boolean {
  if (voice.localService !== true) return false;
  return /^en([-_]|$)/i.test(voice.lang ?? "");
}

export function selectLocalEnglishVoice(
  voices: readonly SpeechVoiceLike[],
): SpeechVoiceLike | null {
  const candidates = voices.filter(isLocalEnglishVoice);
  if (candidates.length === 0) return null;
  return candidates.find((voice) => voice.default === true) ?? candidates[0] ?? null;
}

export interface SpeechSynthesisAdapter {
  getVoices(): SpeechVoiceLike[];
  speak(utterance: SpeechUtteranceLike): void;
  cancel(): void;
  addEventListener?(type: "voiceschanged", listener: () => void): void;
  removeEventListener?(type: "voiceschanged", listener: () => void): void;
}

export interface SpeechUtteranceLike {
  text: string;
  voice?: SpeechVoiceLike | null;
  rate?: number;
  pitch?: number;
  volume?: number;
  onend?: (() => void) | null;
  onerror?: ((error: unknown) => void) | null;
}

export const VOICES_TIMEOUT_MS = 2000;

/**
 * Wait for `voiceschanged` with a bounded timeout. Some platforms populate
 * voices asynchronously; without the wait, a perfectly good local voice is
 * missed and the app shows a false "no local voice" state.
 */
export function waitForVoices(
  synth: SpeechSynthesisAdapter,
  timeoutMs: number = VOICES_TIMEOUT_MS,
): Promise<SpeechVoiceLike[]> {
  const initial = synth.getVoices();
  if (initial.length > 0) return Promise.resolve(initial);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (voices: SpeechVoiceLike[]) => {
      if (settled) return;
      settled = true;
      synth.removeEventListener?.("voiceschanged", onChange);
      clearTimeout(timer);
      resolve(voices);
    };
    const onChange = () => {
      const voices = synth.getVoices();
      if (voices.length > 0) finish(voices);
    };
    synth.addEventListener?.("voiceschanged", onChange);
    const timer = setTimeout(() => finish(synth.getVoices()), timeoutMs);
  });
}
